'use client';

import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { EditorInstance, handleCommandNavigation } from 'novel';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { TextSelection } from 'prosemirror-state';
import { DOMSerializer } from 'prosemirror-model';
import { marked } from 'marked';
import { generateJSON } from '@tiptap/html';
import { defaultExtensions } from '../extensions';
import { configureMarkedWithImageSize } from '../extensions/image-size-extension';
import {
    EditorRoot,
    EditorContent,
    EditorCommand,
    EditorCommandEmpty,
    EditorCommandList,
    EditorCommandItem,
} from 'novel';
import type { SuggestionItem } from 'novel';
import InlineAiPrompt from './InlineAiPrompt';
import { SlashCommandPalette } from './SlashCommandPalette';
import { useSlashCommand } from '../hooks/useSlashCommand';
import { FixedToolbar } from './fixed-toolbar';
import { useChangeIndicators } from '../hooks/useChangeIndicators';
import { useDecorations } from '../hooks/useDecorations';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { getFormattedContent, htmlToMarkdown } from '../utils/content-formatter';
import { scrollToElementIfNeeded, findAndScrollToElement } from '@/utils/scroll';
import { FloatingTableMenu } from './floating-table-menu';
import { Pencil } from 'lucide-react';

/**
 * EditorCore - Self-contained full working editor with toolbar.
 * Includes: FixedToolbar, editor content, slash commands, inline AI prompt.
 * 
 * Parent (MarkdownEditor) focuses on:
 * - Layout (Panel for agent mode, simple div for standard mode)
 * - Business logic (AI dialogs, chat panel, asset browser)
 */

export interface EditorCoreRef {
    highlightAndScrollTo: (text: string) => void;
    getRenderedText: () => string;
    updateEditorContent: (newContent: string, currentPreviousContent?: string) => { oldRenderedText: string; newRenderedText: string } | undefined;
    getSelectionContext: () => { text: string; prefix: string; suffix: string } | null;
    getCursorContext: () => { before: string; after: string; currentParagraph: string; paragraphBefore: string; paragraphAfter: string; isEmptyParagraph: boolean } | null;
    getContent: (format: 'markdown' | 'html') => string;
    insertContent: (content: string) => void;
    hasUnsavedChanges: () => boolean;
    clearUnsavedChanges: () => void;
}

export interface EditorCoreProps {
    // Editor instance (parent needs reference)
    editorInstance: EditorInstance | null;
    setEditorInstance: (editor: EditorInstance) => void;

    // External state (controlled by parent)
    isReadOnly: boolean;
    isAiProcessing: boolean;

    // Content initialization
    getInitialContent: () => any;
    getExtensions: () => any[];

    // Content format for getContent()
    format: 'markdown' | 'html' | 'json';

    // Business-level handlers (need external dependencies)
    handleImagePaste: (event: any) => boolean;

    // Business callbacks
    onContentUpdate?: (editor: EditorInstance) => void;
    onUnsavedChanges?: (value: boolean) => void;
    onSaveStatusChange?: (status: string) => void;
    onAutoSave?: () => void; // Called when user focus truly leaves editor
    // NOTE: Selection/cursor context is tracked internally and passed directly to onAiEdit

    // Change indicators
    onChangeIndicatorsActivate?: (activateFn: () => void) => void;

    // AI features (optional)
    onAiEdit?: (prompt: string, messages?: any, selectionContext?: any, cursorContext?: any, signal?: AbortSignal) => Promise<any>;
    chatPanelSendMessage?: (message: string) => Promise<boolean>;

    // Novel command items
    suggestionItems: SuggestionItem[];

    // === TOOLBAR PROPS ===
    saveStatus: string;
    wordCount: number | null;
    // hasUnsavedChanges is now managed internally
    isUploadingImage?: boolean;
    isAgentMode?: boolean;
    onToggleAgentMode?: () => void;
    onInsertAsset?: () => void;
    onSave?: () => void;
    onBlurSave?: () => void; // Trigger blur-save to create 'manual edit' history entry
    onAICommand?: (type: 'generate' | 'refine' | 'summarize-title' | 'generate-image') => void;

    // Content for updateEditorContent
    previousContent?: string;

    // Mobile mode: hide the fixed toolbar
    hideToolbar?: boolean;

    // Mobile voice input state (prevents keyboard)
    isVoiceInputActive?: boolean;

    // Custom input mode (e.g. 'none' for mobile custom keyboard)
    inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
}

export const EditorCore = forwardRef<EditorCoreRef, EditorCoreProps>(({
    editorInstance,
    setEditorInstance,
    isReadOnly,
    isAiProcessing,
    getInitialContent,
    getExtensions,
    format,
    handleImagePaste,
    onContentUpdate,
    onUnsavedChanges,
    onSaveStatusChange,
    onAutoSave,
    onChangeIndicatorsActivate,
    onAiEdit,
    chatPanelSendMessage,
    suggestionItems,
    // Toolbar props
    saveStatus,
    wordCount,
    // hasUnsavedChanges is now internal
    isUploadingImage = false,
    isAgentMode = false,
    onToggleAgentMode,
    onInsertAsset,
    onSave,
    onBlurSave,
    onAICommand,
    previousContent = '',
    hideToolbar = false,
    isVoiceInputActive = false,
    inputMode,
}, ref) => {
    // === INTERNAL STATE ===

    // Internal editor ref (no longer passed as prop)
    const editorRef = useRef<HTMLDivElement>(null);
    const markdownTextareaRef = useRef<HTMLTextAreaElement>(null);

    // View mode state (internalized)
    const [viewMode, setViewMode] = useState<'visual' | 'markdown' | 'html'>('visual');
    const [rawContent, setRawContent] = useState('');
    const [htmlContent, setHtmlContent] = useState('');

    // Highlight decoration state for highlightAndScrollTo
    const [highlightDecorations, setHighlightDecorations] = useState<DecorationSet>(DecorationSet.empty);
    const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Focus tracking for preserved selection
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    // Decorations hook (manages active paragraph, preserved selection)
    const {
        decorations,
        setPreservedSelection,
        trackActiveParagraph,
    } = useDecorations({
        editorInstance,
        isAiProcessing,
        isEditorFocused,
    });

    // Inline AI prompt state
    const [isAiPromptOpen, setIsAiPromptOpen] = useState(false);

    // Change indicator tooltip state
    const [tooltip, setTooltip] = useState<{ content: string; x: number; y: number } | null>(null);

    // Internal unsaved changes tracking (single source of truth)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const hasUnsavedChangesRef = useRef(false); // Ref for stable access in callbacks

    // Selection context refs (populated on selection change)
    const selectionContextRef = useRef<{ text: string; prefix: string; suffix: string } | null>(null);
    const cursorContextRef = useRef<{ before: string; after: string; currentParagraph: string; paragraphBefore: string; paragraphAfter: string; isEmptyParagraph: boolean } | null>(null);

    // Get cursor context for AI editing (when no text is selected)
    const getCursorContext = React.useCallback((editor: EditorInstance | null) => {
        if (!editor) return null;

        const { state, view } = editor;
        const { selection } = state;
        const { from, to } = selection;

        // Only proceed if it's a cursor (no selection)
        if (from !== to) return null;

        const { doc } = state;
        const $pos = doc.resolve(from);
        const currentNode = $pos.parent;

        if (!currentNode.isTextblock) return null;

        const paragraphStart = $pos.start();
        const paragraphEnd = $pos.end();

        const before = doc.textBetween(paragraphStart, from);
        const after = doc.textBetween(from, paragraphEnd);

        // Serialize current node to Markdown for precise context
        const domSerializer = DOMSerializer.fromSchema(state.schema);
        const serializeNodeToMarkdown = (node: any) => {
            const dom = domSerializer.serializeNode(node);
            const div = document.createElement('div');
            div.appendChild(dom);
            return htmlToMarkdown(div.innerHTML);
        };

        const currentParagraphMarkdown = serializeNodeToMarkdown(currentNode);

        // Find paragraph before
        let paragraphBefore = '';
        if (paragraphStart > 1) {
            const posBefore = state.doc.resolve(paragraphStart - 2);
            const nodeBefore = posBefore.parent;
            if (nodeBefore.isTextblock) {
                // Serialize entire node before to Markdown
                paragraphBefore = serializeNodeToMarkdown(nodeBefore);
            }
        }

        // Find paragraph after
        let paragraphAfter = '';
        if (paragraphEnd < doc.content.size - 1) {
            const posAfter = state.doc.resolve(paragraphEnd + 2);
            const nodeAfter = posAfter.parent;
            if (nodeAfter.isTextblock) {
                // Serialize entire node after to Markdown
                paragraphAfter = serializeNodeToMarkdown(nodeAfter);
            }
        }

        const result = {
            before,
            after,
            currentParagraph: currentParagraphMarkdown,
            paragraphBefore: paragraphBefore,
            paragraphAfter: paragraphAfter,
            isEmptyParagraph: currentParagraphMarkdown.trim() === '',
        };

        console.log('[EditorCore] getCursorContext extracted', {
            currentParagraph: JSON.stringify(result.currentParagraph),
            isEmpty: result.isEmptyParagraph
        });

        return result;
    }, []);

    // Handle selection changes - track context for AI editing (internal)
    const handleSelectionChange = React.useCallback((editor: EditorInstance) => {
        const { state } = editor;
        const { from, to } = state.selection;

        if (from !== to) {
            // It's a selection - get context for AI
            // We serialize to Markdown to ensure we match the backend's content format
            // which enables accurate patching in applyReplacement
            const domSerializer = DOMSerializer.fromSchema(state.schema);
            const serializeFragmentToMarkdown = (fragment: any) => {
                const dom = domSerializer.serializeFragment(fragment);
                const div = document.createElement('div');
                div.appendChild(dom);
                return htmlToMarkdown(div.innerHTML);
            };

            // Serialize selection
            const selectionSlice = state.doc.slice(from, to);
            const selectedText = serializeFragmentToMarkdown(selectionSlice.content);

            // Serialize prefix (context before)
            // We explicitly grab a slice to maintain Markdown fidelity for context matching
            const prefixStart = Math.max(0, from - 100);
            const prefixSlice = state.doc.slice(prefixStart, from);
            const prefix = serializeFragmentToMarkdown(prefixSlice.content);

            // Serialize suffix (context after)
            const suffixEnd = Math.min(state.doc.content.size, to + 100);
            const suffixSlice = state.doc.slice(to, suffixEnd);
            const suffix = serializeFragmentToMarkdown(suffixSlice.content);

            selectionContextRef.current = { text: selectedText, prefix, suffix };
            cursorContextRef.current = null;

            console.log('[EditorCore] Selection context captured', {
                textSize: selectedText.length,
                textSnippet: JSON.stringify(selectedText.substring(0, 50))
            });
        } else {
            // It's a cursor - get cursor context for AI
            selectionContextRef.current = null;
            cursorContextRef.current = getCursorContext(editor);
        }
    }, [getCursorContext]);

    // Internal view mode change handler
    const handleViewModeChange = useCallback((mode: 'visual' | 'markdown' | 'html') => {
        if (!editorInstance) return;

        // Switching FROM visual mode
        if (viewMode === 'visual') {
            const currentContent = getFormattedContent(editorInstance, format) as string;

            if (mode === 'markdown') {
                setRawContent(currentContent);
            } else if (mode === 'html') {
                const html = marked.parse(currentContent) as string;
                setHtmlContent(html);
            }
        }
        // Switching FROM markdown mode
        else if (viewMode === 'markdown') {
            if (mode === 'visual') {
                // Configure marked with image size extension
                configureMarkedWithImageSize();
                const html = marked.parse(rawContent) as string;
                const jsonContent = generateJSON(html, defaultExtensions);
                editorInstance.commands.setContent(jsonContent, false);
            } else if (mode === 'html') {
                const html = marked.parse(rawContent) as string;
                setHtmlContent(html);
            }
        }

        setViewMode(mode);
    }, [editorInstance, viewMode, format, rawContent]);

    // Internal raw content change handler
    const handleRawContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRawContent(e.target.value);
    };

    // Slash command hook
    const slashCommand = useSlashCommand({ editorInstance });

    // === UNIFIED COMMAND HANDLERS ===
    // These handlers ensure consistent behavior regardless of trigger source
    // (keyboard shortcut, slash menu, toolbar, etc.)

    const handleOpenAiPrompt = React.useCallback(() => {
        // Trigger blur-save before showing AI prompt (creates 'manual edit' history entry)
        console.log('[EditorCore] handleOpenAiPrompt called', { hasUnsavedChanges, hasOnBlurSave: !!onBlurSave });
        if (hasUnsavedChanges && onBlurSave) {
            console.log('[EditorCore] Triggering blur-save before AI prompt');
            onBlurSave();
        }
        setIsAiPromptOpen(true);
    }, [hasUnsavedChanges, onBlurSave]);

    const handleAICommand = React.useCallback((type: 'generate' | 'refine' | 'summarize-title' | 'generate-image') => {
        // Trigger blur-save before AI command (creates 'manual edit' history entry)
        if (hasUnsavedChanges && onBlurSave) {
            console.log('[EditorCore] Triggering blur-save before AI command:', type);
            onBlurSave();
        }
        onAICommand?.(type);
    }, [hasUnsavedChanges, onBlurSave, onAICommand]);

    // Keyboard shortcuts hook - uses unified handlers
    const keyboardShortcuts = useKeyboardShortcuts({
        editorInstance,
        isAgentMode,
        onSave,
        onAICommand: handleAICommand,
        onOpenAiPrompt: handleOpenAiPrompt,
    });

    // Change indicators hook (internal management)
    const changeIndicators = useChangeIndicators({
        editorInstance,
        showChangeIndicators: true,  // Always enabled
        isAgentMode,
        isAiProcessing,
    });

    // Expose activator function to parent (for when AI updates content)
    React.useEffect(() => {
        onChangeIndicatorsActivate?.(changeIndicators.activateIndicators);
    }, [onChangeIndicatorsActivate, changeIndicators.activateIndicators]);

    // Listen for inline AI prompt from slash command - uses unified handler
    React.useEffect(() => {
        document.addEventListener('invoke-ai-prompt', handleOpenAiPrompt);
        return () => {
            document.removeEventListener('invoke-ai-prompt', handleOpenAiPrompt);
        };
    }, [handleOpenAiPrompt]);

    // Handle change indicator tooltips
    React.useEffect(() => {
        if (!editorRef.current) return;

        // Debounce utility for performance
        const debounce = <T extends (...args: any[]) => any>(func: T, delay: number): T => {
            let timeoutId: NodeJS.Timeout;
            return function (this: any, ...args: any[]) {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => func.apply(this, args), delay);
            } as T;
        };

        const handleMouseMove = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const wrapper = target.closest('.change-indicator-wrapper');

            if (wrapper) {
                const rect = wrapper.getBoundingClientRect();
                const mouseX = event.clientX;

                // Check if mouse is over the indicator area (left margin)
                if (mouseX >= rect.left - 20 && mouseX <= rect.left + 10) {
                    const tooltipContent = wrapper.getAttribute('data-tooltip');
                    if (tooltipContent) {
                        setTooltip({
                            content: tooltipContent,
                            x: event.clientX + 10,
                            y: event.clientY - 10
                        });
                    }
                } else {
                    setTooltip(null);
                }
            } else {
                setTooltip(null);
            }
        };

        const handleMouseLeave = () => {
            setTooltip(null);
        };

        // PERFORMANCE: Debounce mousemove to reduce event handler frequency by ~95%
        // 50ms delay is imperceptible to users but significantly reduces overhead
        const debouncedMouseMove = debounce(handleMouseMove, 50);

        const editorElement = editorRef.current;
        editorElement.addEventListener('mousemove', debouncedMouseMove);
        editorElement.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            editorElement.removeEventListener('mousemove', debouncedMouseMove);
            editorElement.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [editorInstance]);

    // === EXPOSED REF FUNCTIONS ===
    // These functions are available to parent components via ref
    useImperativeHandle(ref, () => ({
        highlightAndScrollTo: (text: string) => {
            if (!editorInstance || !text) return;

            const { doc } = editorInstance.state;

            // Search through text nodes to find the exact document position
            // (plain text indexOf doesn't account for ProseMirror node boundaries)
            let from = -1;
            let to = -1;

            doc.descendants((node, pos) => {
                if (from !== -1) return false; // Already found, stop searching

                if (node.isText && node.text) {
                    const idx = node.text.indexOf(text);
                    if (idx !== -1) {
                        from = pos + idx;
                        to = from + text.length;
                        return false; // Stop searching
                    }
                }
                return true; // Continue searching
            });

            // If not found in single node, text might span multiple nodes
            // Fall back to building full text with position tracking
            if (from === -1) {
                const fullText = doc.textBetween(0, doc.content.size, '');
                const plainIdx = fullText.indexOf(text);

                if (plainIdx !== -1) {
                    // Walk through document to map plain text position to doc position
                    let charCount = 0;
                    doc.descendants((node, pos) => {
                        if (from !== -1) return false;

                        if (node.isText && node.text) {
                            const nodeStart = charCount;
                            const nodeEnd = charCount + node.text.length;

                            // Check if our target starts in this node
                            if (plainIdx >= nodeStart && plainIdx < nodeEnd) {
                                from = pos + (plainIdx - nodeStart);
                                to = from + text.length;
                                return false;
                            }
                            charCount += node.text.length;
                        }
                        return true;
                    });
                }
            }

            if (from === -1) return; // Text not found

            try {
                // Create a transaction to set selection
                const tr = editorInstance.state.tr;
                tr.setSelection(TextSelection.create(doc, from, to));
                editorInstance.view.dispatch(tr);

                // Create and apply decoration
                const decoration = Decoration.inline(from, to, { class: 'highlight' });
                const newDecorations = DecorationSet.create(doc, [decoration]);
                setHighlightDecorations(newDecorations);

                // Use unified scroll utility - scroll to the selection DOM element if not visible
                setTimeout(() => {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        const element = range.startContainer.parentElement;
                        scrollToElementIfNeeded(element);
                    }
                }, 50);
            } catch (error) {
                console.error('[EditorCore] Error creating highlight:', error);
            }

            // Clear any existing highlight timeout
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
            }

            // Remove highlight after a delay
            highlightTimeoutRef.current = setTimeout(() => {
                setHighlightDecorations(DecorationSet.empty);
                highlightTimeoutRef.current = null;
            }, 2500);
        },

        // Get the current rendered/visible text from the editor
        getRenderedText: (): string => {
            if (!editorInstance) return '';
            const { doc } = editorInstance.state;
            return doc.textBetween(0, doc.content.size, '\n');
        },

        updateEditorContent: (newContent: string, currentPreviousContent?: string): { oldRenderedText: string; newRenderedText: string } | undefined => {
            if (!editorInstance) return undefined;

            // Capture the OLD rendered text BEFORE updating
            const { doc: oldDoc } = editorInstance.state;
            const oldRenderedText = oldDoc.textBetween(0, oldDoc.content.size, '\n');

            // Use the passed previous content or fall back to the prop
            const effectivePreviousContent = currentPreviousContent || previousContent || '';

            // Configure marked with image size extension
            configureMarkedWithImageSize();

            const html = marked.parse(newContent) as string;
            const jsonContent = generateJSON(html, defaultExtensions);
            editorInstance.commands.setContent(jsonContent, false);

            // Capture the NEW rendered text AFTER updating
            const { doc: newDoc } = editorInstance.state;
            const newRenderedText = newDoc.textBetween(0, newDoc.content.size, '\n');

            // Clear preserved selection since document content has changed
            // (old selection coordinates are now stale)
            setPreservedSelection(null);

            // Update the extension options directly and trigger update
            const changeIndicatorExt = editorInstance.extensionManager.extensions.find(ext => ext.name === 'changeIndicator');
            if (changeIndicatorExt) {
                changeIndicatorExt.options.enabled = true;  // Re-enable for AI edits
                changeIndicatorExt.options.previousContent = effectivePreviousContent;
                changeIndicatorExt.options.currentContent = newContent;
            }

            // Trigger change indicator update
            const tr = editorInstance.state.tr;
            tr.setMeta('updateChangeIndicators', true);

            editorInstance.view.dispatch(tr);

            // Mark that change indicators are now active (showing AI changes)
            changeIndicators.activateIndicators?.();

            // Auto-scroll to the first changed section after a short delay
            // This ensures the user can see what was changed
            setTimeout(() => {
                // The change indicators use wrapper classes with -- modifiers
                const changeSelectors = '.change-indicator-wrapper--added, .change-indicator-wrapper--modified, .change-indicator-wrapper--deleted, .change-indicator-deleted-placeholder';

                // Use unified scroll utility - finds element and scrolls if not visible
                findAndScrollToElement(changeSelectors, editorRef.current);
            }, 100); // Short delay to allow decorations to be rendered

            // Return the old and new rendered text for diffing
            return { oldRenderedText, newRenderedText };
        },

        // Expose selection/cursor context for external AI features
        getSelectionContext: () => selectionContextRef.current,
        getCursorContext: () => cursorContextRef.current,

        // Get content in specified format
        getContent: (fmt: 'markdown' | 'html') => {
            if (!editorInstance) return '';
            return getFormattedContent(editorInstance, fmt) as string;
        },

        // Insert content at cursor/selection
        insertContent: (content: string) => {
            if (!editorInstance) return;

            const start = editorInstance.state.selection.from;
            editorInstance.chain()
                .focus()
                .insertContent(content)
                .run();

            // Use setTimeout to ensure re-renders (like setSaveStatus) have settled
            // and the DOM is ready for a scroll event.
            setTimeout(() => {
                const end = editorInstance.state.selection.to;
                editorInstance.chain()
                    .setTextSelection({ from: start, to: end })
                    .scrollIntoView()
                    .run();
            }, 50);
        },

        // Expose unsaved changes status
        hasUnsavedChanges: () => hasUnsavedChangesRef.current,

        // Clear unsaved changes flag (called after save)
        clearUnsavedChanges: () => {
            setHasUnsavedChanges(false);
            hasUnsavedChangesRef.current = false;
        },
    }), [editorInstance, previousContent, changeIndicators.activateIndicators, format]);

    // === INTERNAL HANDLERS ===

    const handleSlashExecute = useCallback((item: SuggestionItem) => {
        if (!editorInstance) return;
        const posNum = editorInstance.state.selection.from;
        const range = { from: posNum, to: posNum } as any;
        (item as any).command({ editor: (editorInstance as unknown) as any, range });
        slashCommand.close();
    }, [editorInstance, slashCommand]);

    const handleBlur = useCallback(() => {
        if (editorInstance) {
            const { from, to } = editorInstance.state.selection;
            setPreservedSelection({ from, to });
        }
        return false;
    }, [editorInstance, setPreservedSelection]);

    // Unified smart blur detector - shared between Visual and Markdown modes
    const detectSmartBlur = useCallback((event: any) => {
        const relatedTarget = event.relatedTarget as HTMLElement;

        // Check if focus is moving to editor-related elements
        const editorContainer = editorRef.current;
        const markdownContainer = markdownTextareaRef.current;

        const isMovingToEditorElement = relatedTarget && (editorContainer?.contains(relatedTarget) || markdownContainer?.contains(relatedTarget));
        const isMovingToSlashMenu = relatedTarget?.closest('.slash-command-palette');
        const isMovingToAIDialog = relatedTarget?.closest('[role="dialog"]');
        const isMovingToToolbar = relatedTarget?.closest('.editor-toolbar');

        // Only switch focus state if moving to external elements
        const isLeavingEditorContext = !isMovingToEditorElement &&
            !isMovingToSlashMenu &&
            !isMovingToAIDialog &&
            !isMovingToToolbar;

        return isLeavingEditorContext;
    }, [editorRef, markdownTextareaRef]);

    // Smart blur handler (Visual Mode/ProseMirror)
    const handleDomBlur = useCallback((view: any, event: FocusEvent) => {
        // Preserve selection
        if (editorInstance) {
            const { from, to } = editorInstance.state.selection;
            setPreservedSelection({ from, to });
        }

        if (detectSmartBlur(event)) {
            setIsEditorFocused(false);

            // Trigger auto-save if needed
            if (hasUnsavedChanges && onAutoSave) {
                console.log('[EditorCore] Focus left visual editor context, triggering auto-save');
                onAutoSave();
            }
        }

        return false;
    }, [editorInstance, hasUnsavedChanges, onAutoSave, detectSmartBlur, setPreservedSelection]);

    // Smart blur handler (Markdown Mode)
    const handleTextareaBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        if (detectSmartBlur(event)) {
            setIsEditorFocused(false);

            // Trigger auto-save if needed
            if (hasUnsavedChanges && onAutoSave) {
                console.log('[EditorCore] Focus left markdown editor context, triggering auto-save');
                onAutoSave();
            }
        }
    }, [hasUnsavedChanges, onAutoSave, detectSmartBlur]);

    const handleFocus = useCallback(() => {
        // Selection restoration is handled by the decorations hook
        setIsEditorFocused(true);
        setPreservedSelection(null);
        return false;
    }, [setPreservedSelection]);

    return (
        <div className="flex flex-col h-full w-full">
            {/* Fixed Toolbar (hidden in mobile mode) */}
            {!hideToolbar && (
                <FixedToolbar
                    editor={editorInstance}
                    onAICommand={handleAICommand}
                    saveStatus={saveStatus}
                    wordCount={wordCount}
                    isAgentMode={isAgentMode}
                    onToggleAgentMode={onToggleAgentMode}
                    onInsertAsset={onInsertAsset}
                    onSave={onSave}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isAiProcessing={isAiProcessing}
                    isUploadingImage={isUploadingImage}
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                    isReadOnly={isReadOnly}
                />
            )}

            {/* Editor Area Wrapper - Contains both the scrollable editor and the sticky overlay */}
            <div className="flex-1 relative overflow-hidden editor-area-wrapper">
                {/* STICKY BLUR OVERLAY - Positioned over the entire editor area */}
                {!isEditorFocused && !isReadOnly && !isAiProcessing && !slashCommand.isOpen && !isAiPromptOpen && (
                    <div
                        className="focus-not-active-overlay"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditorFocused(true);
                            // Focus the appropriate editor based on current mode
                            if (viewMode === 'visual' && editorInstance) {
                                editorInstance.commands.focus();
                            } else if (viewMode === 'markdown' && markdownTextareaRef.current) {
                                markdownTextareaRef.current.focus();
                            }
                        }}
                    >
                        <div className="focus-not-active-badge">
                            <Pencil className="h-4 w-4" />
                            <span>Click to resume editing</span>
                        </div>
                    </div>
                )}

                {/* Editor Content Area - Scrollable root */}
                <div className={`novel-editor-root h-full overflow-auto relative ${!isEditorFocused && !isReadOnly && !isAiProcessing && !slashCommand.isOpen && !isAiPromptOpen ? 'is-blurred' : ''}`}>
                    {/* Read-only overlay */}
                    {isReadOnly && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-opacity-50 bg-gray-100">
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center border-2 border-gray-300">
                                <h3 className="text-lg font-semibold mb-2 text-gray-800">Editor Disabled</h3>
                                <p className="text-sm text-gray-600">Please finish editing the title first</p>
                            </div>
                        </div>
                    )}

                    {/* Visual Editor */}
                    <div className={`editor-container ${isAiProcessing || isReadOnly ? 'is-processing' : ''} ${viewMode !== 'visual' ? 'hidden' : ''}`}>
                        <EditorRoot>
                            <EditorContent
                                ref={editorRef}
                                initialContent={getInitialContent()}
                                extensions={getExtensions()}
                                editable={!isReadOnly && !isAiProcessing}
                                className="relative w-full border-0 bg-white"
                                editorProps={{
                                    decorations: () => decorations,
                                    handleDOMEvents: {
                                        keydown: (_view, event) => {
                                            if (slashCommand.handleKeydown(event)) return true;
                                            if (keyboardShortcuts.handleKeyboardShortcuts(event)) return true;
                                            return handleCommandNavigation(event);
                                        },
                                        paste: (_view, event) => handleImagePaste(event),
                                        blur: handleDomBlur,
                                        focus: handleFocus,
                                    },
                                    attributes: {
                                        class: "prose prose-lg prose-stone dark:prose-invert prose-headings:font-title font-default focus:outline-none max-w-full p-4",
                                        inputMode: inputMode || (isVoiceInputActive ? 'none' : undefined),
                                    },
                                    transformPastedHTML: (html) => {
                                        return html.replace(/<img.*?>/g, (match) => {
                                            return match.replace(/width="(\d+)"/, 'width="100%"').replace(/height="(\d+)"/, '');
                                        });
                                    }
                                }}
                                onCreate={({ editor }) => {
                                    setEditorInstance(editor);
                                }}
                                onUpdate={({ editor, transaction }) => {
                                    onContentUpdate?.(editor);
                                    trackActiveParagraph(editor);
                                    setPreservedSelection(null);
                                    selectionContextRef.current = null;

                                    if (transaction.docChanged) {
                                        // Update internal state and ref
                                        setHasUnsavedChanges(true);
                                        hasUnsavedChangesRef.current = true;
                                        // Notify parent
                                        onUnsavedChanges?.(true);
                                        onSaveStatusChange?.('unsaved');
                                    }

                                    // Auto-clear change indicators on manual editing
                                    changeIndicators.handleEditorUpdate(transaction);
                                }}
                                onSelectionUpdate={({ editor }) => {
                                    trackActiveParagraph(editor);
                                    handleSelectionChange(editor);
                                }}
                            >
                                <EditorCommand className="z-50 h-auto max-h-[330px] w-72 overflow-y-auto rounded-md border border-stone-200 bg-white px-1 py-2 shadow-md transition-all">
                                    <EditorCommandEmpty className="px-2 text-stone-500">
                                        No results
                                    </EditorCommandEmpty>
                                    <EditorCommandList>
                                        {suggestionItems.map((item) => (
                                            <EditorCommandItem
                                                key={item.title}
                                                value={item.title}
                                                onCommand={(val) => item.command?.(val)}
                                                className="flex w-full items-center space-x-2 rounded-md px-2 py-1 text-left text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                                            >
                                                <div className="flex h-10 w-10 items-center justify-center rounded-md border border-stone-200 bg-white">
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <p className="font-medium">{item.title}</p>
                                                    <p className="text-xs text-stone-500">{item.description}</p>
                                                </div>
                                            </EditorCommandItem>
                                        ))}
                                    </EditorCommandList>
                                </EditorCommand>

                                {isAiPromptOpen && !isAiProcessing && editorInstance && (
                                    <InlineAiPrompt
                                        editor={editorInstance}
                                        sendMessage={chatPanelSendMessage}
                                        onCommand={async (prompt) => {
                                            if (onAiEdit) {
                                                await onAiEdit(prompt, undefined, selectionContextRef.current, cursorContextRef.current);
                                            } else {
                                                throw new Error('AI editing is not available');
                                            }
                                        }}
                                        onClose={() => setIsAiPromptOpen(false)}
                                    />
                                )}
                            </EditorContent>
                        </EditorRoot>

                        {/* Floating table editing menu - appears above table when cursor is inside */}
                        <FloatingTableMenu editor={editorInstance} />
                    </div>

                    {/* Markdown view mode */}
                    {viewMode === 'markdown' && (
                        <div className="flex-1 h-full bg-slate-50 p-6 overflow-hidden relative">
                            <textarea
                                ref={markdownTextareaRef}
                                value={rawContent}
                                onChange={handleRawContentChange}
                                onBlur={handleTextareaBlur}
                                onFocus={() => setIsEditorFocused(true)}
                                className="w-full h-full min-h-[500px] p-4 font-mono text-sm bg-white border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                placeholder="Enter markdown content..."
                                spellCheck={false}
                            />
                        </div>
                    )}
                </div>

                {/* Slash Command Palette - Positioned at wrapper level to avoid clipping and context issues */}
                <SlashCommandPalette
                    isOpen={slashCommand.isOpen}
                    position={slashCommand.position}
                    items={slashCommand.items}
                    query={slashCommand.query}
                    onQueryChange={slashCommand.setQuery}
                    selected={slashCommand.selected}
                    onSelectItem={slashCommand.setSelected}
                    onExecuteItem={handleSlashExecute}
                    menuRef={slashCommand.menuRef}
                    inputRef={slashCommand.inputRef}
                />
            </div>

            {/* Change indicator tooltip */}
            {tooltip && (
                <div
                    className="fixed z-50 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                    }}
                >
                    {tooltip.content}
                </div>
            )}
        </div>
    );
});

export default EditorCore;
